/**
 * EventManager handles all event-related operations including hotspot clicks,
 * keyboard navigation, mobile interactions, and video events
 */
export class EventManager {
  constructor() {
    this.eventListeners = new Map(); // Track active event listeners
    this.eventHandlers = new Map(); // Store event handler functions
  }

  /**
   * Setup all event listeners for the dynamic step system
   * @param {Object} callbacks - Object containing callback functions
   */
  setupEventListeners(callbacks) {
    const {
      hotspotClickCallback,
      nextStepCallback,
      prevStepCallback,
      navigationCallback,
    } = callbacks;

    // Setup hotspot event listeners
    this.setupHotspotEvents(hotspotClickCallback);

    // Setup keyboard navigation
    this.setupKeyboardEvents(nextStepCallback, prevStepCallback);

    // Setup mobile hotspot events
    this.setupMobileHotspotEvents(hotspotClickCallback);
  }

  /**
   * Setup desktop hotspot event listeners
   * @param {Function} hotspotClickCallback - Callback for hotspot clicks
   */
  setupHotspotEvents(hotspotClickCallback) {
    const handleHotspotClick = (e) => {
      const hotspot = e.target.closest(".hotspot");
      if (hotspot) {
        e.preventDefault();
        e.stopPropagation();
        hotspotClickCallback(hotspot);
      }
    };

    // Use event delegation for better performance
    document.addEventListener("click", handleHotspotClick);
    this.eventListeners.set("hotspot-click", handleHotspotClick);
  }

  /**
   * Setup mobile hotspot event listeners
   * @param {Function} hotspotClickCallback - Callback for hotspot clicks
   */
  setupMobileHotspotEvents(hotspotClickCallback) {
    const handleMobileHotspotClick = (e) => {
      const mobileHotspot = e.target.closest(".mobile-hotspot-button");
      if (mobileHotspot) {
        e.preventDefault();
        e.stopPropagation();

        // Create a fake hotspot element with the same data attributes
        const fakeHotspot = document.createElement("div");
        fakeHotspot.setAttribute(
          "data-hotspot-id",
          mobileHotspot.getAttribute("data-hotspot-id"),
        );
        fakeHotspot.className = "hotspot mobile-hotspot-proxy";

        hotspotClickCallback(fakeHotspot);
      }
    };

    document.addEventListener("click", handleMobileHotspotClick);
    this.eventListeners.set("mobile-hotspot-click", handleMobileHotspotClick);

    console.log("[Mobile] hotspot events setup");
  }

  /**
   * Setup keyboard navigation events
   * @param {Function} nextStepCallback - Callback for next step
   * @param {Function} prevStepCallback - Callback for previous step
   */
  setupKeyboardEvents(nextStepCallback, prevStepCallback) {
    const handleKeydown = (e) => {
      // Don't interfere with form inputs
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      switch (e.key) {
        case "ArrowRight":
        case " ": // Spacebar
          e.preventDefault();
          nextStepCallback();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevStepCallback();
          break;
        case "Escape":
          // Handle escape key for closing modals, panels, etc.
          this.handleEscapeKey();
          break;
      }
    };

    document.addEventListener("keydown", handleKeydown);
    this.eventListeners.set("keyboard", handleKeydown);
  }

  /**
   * Setup video event listeners for a specific video element
   * @param {HTMLVideoElement} video - Video element
   * @param {Function} videoEndCallback - Callback when video ends
   * @param {number} stepId - Step ID for the video
   */
  setupVideoEvents(video, videoEndCallback, stepId) {
    if (!video || video.tagName !== "VIDEO") {
      console.warn(`!! Invalid video element for step ${stepId}`);
      return;
    }

    // Remove existing video event listeners for this step first
    const videoEventKey = `video-${stepId}`;
    const existingListener = this.eventListeners.get(videoEventKey);
    if (existingListener && existingListener.events) {
      for (const [eventType, handler] of Object.entries(existingListener.events)) {
        existingListener.element.removeEventListener(eventType, handler);
      }
      this.eventListeners.delete(videoEventKey);
    }

    const handleVideoEnd = () => {
      console.log(`[Video] ended for step ${stepId}`);
      videoEndCallback(stepId);
    };

    const handleVideoError = (e) => {
      console.error(`[Video] error for step ${stepId}:`, e);
    };

    const handleVideoLoadStart = () => {
      console.log(`[Video] loading started for step ${stepId}`);
    };

    const handleVideoCanPlay = () => {
      console.log(`[Video] can play for step ${stepId}`);
    };

    // Add event listeners
    video.addEventListener("ended", handleVideoEnd);
    video.addEventListener("error", handleVideoError);
    video.addEventListener("loadstart", handleVideoLoadStart);
    video.addEventListener("canplay", handleVideoCanPlay);

    // Store references for cleanup
    this.eventListeners.set(videoEventKey, {
      element: video,
      events: {
        ended: handleVideoEnd,
        error: handleVideoError,
        loadstart: handleVideoLoadStart,
        canplay: handleVideoCanPlay,
      },
    });
  }

  /**
   * Setup panel and tab event listeners
   * @param {HTMLElement} panel - Panel element
   * @param {number} stepId - Step ID
   * @param {Function} tabClickCallback - Callback for tab clicks
   * @param {Function} backButtonCallback - Callback for back button clicks
   */
  setupPanelEvents(panel, stepId, tabClickCallback, backButtonCallback) {
    // Tab click events
    const tabButtons = panel.querySelectorAll(".tab-button");
    tabButtons.forEach((tabButton) => {
      const handleTabClick = () => {
        tabClickCallback(tabButton, stepId);
      };

      tabButton.addEventListener("click", handleTabClick);

      // Store reference for cleanup
      const tabEventKey = `tab-${stepId}-${tabButton.getAttribute("data-tab")}`;
      this.eventListeners.set(tabEventKey, {
        element: tabButton,
        event: "click",
        handler: handleTabClick,
      });
    });

    // Back button events
    const backButton = panel.querySelector(".back-button");
    if (backButton) {
      const handleBackClick = () => {
        backButtonCallback(stepId);
      };

      backButton.addEventListener("click", handleBackClick);

      const backEventKey = `back-${stepId}`;
      this.eventListeners.set(backEventKey, {
        element: backButton,
        event: "click",
        handler: handleBackClick,
      });
    }

    // Close button events
    const closeButton = panel.querySelector(".close-panel");
    if (closeButton) {
      const handleCloseClick = () => {
        panel.classList.remove("visible");
      };

      closeButton.addEventListener("click", handleCloseClick);

      const closeEventKey = `close-${stepId}`;
      this.eventListeners.set(closeEventKey, {
        element: closeButton,
        event: "click",
        handler: handleCloseClick,
      });
    }
  }

  /**
   * Handle escape key press
   */
  handleEscapeKey() {
    // Close any open modals
    const modals = document.querySelectorAll(".contact-modal:not(.hidden)");
    modals.forEach((modal) => {
      modal.classList.add("hidden");
      document.body.style.overflow = "";
    });

    // Close any visible panels
    const visiblePanels = document.querySelectorAll(
      ".info-panel.visible, .detail-panel.visible",
    );
    visiblePanels.forEach((panel) => {
      panel.classList.remove("visible");
    });
  }

  /**
   * Setup duration-based transition events
   * @param {number} stepId - Step ID
   * @param {number} duration - Duration in milliseconds
   * @param {Function} transitionCallback - Callback when duration expires
   */
  setupDurationTransition(stepId, duration, transitionCallback) {
    const timeoutId = setTimeout(() => {
      transitionCallback(stepId);
    }, duration);

    // Store timeout ID for cleanup
    this.eventListeners.set(`duration-${stepId}`, {
      type: "timeout",
      id: timeoutId,
    });
  }

  /**
   * Remove event listeners for a specific step
   * @param {number} stepId - Step ID to cleanup
   */
  cleanupStepEvents(stepId) {
    const keysToRemove = [];

    for (const [key, listener] of this.eventListeners.entries()) {
      if (key.includes(`-${stepId}`)) {
        if (listener.type === "timeout") {
          clearTimeout(listener.id);
        } else if (listener.element && listener.event && listener.handler) {
          listener.element.removeEventListener(
            listener.event,
            listener.handler,
          );
        } else if (listener.events) {
          // Video events cleanup
          for (const [eventType, handler] of Object.entries(listener.events)) {
            listener.element.removeEventListener(eventType, handler);
          }
        }
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => this.eventListeners.delete(key));
  }

  /**
   * Remove all event listeners
   */
  cleanup() {
    for (const [key, listener] of this.eventListeners.entries()) {
      if (listener.type === "timeout") {
        clearTimeout(listener.id);
      } else if (listener.element && listener.event && listener.handler) {
        listener.element.removeEventListener(listener.event, listener.handler);
      } else if (listener.events) {
        // Video events cleanup
        for (const [eventType, handler] of Object.entries(listener.events)) {
          listener.element.removeEventListener(eventType, handler);
        }
      } else if (typeof listener === "function") {
        // Global event listeners
        document.removeEventListener("click", listener);
        document.removeEventListener("keydown", listener);
      }
    }

    this.eventListeners.clear();
  }

  /**
   * Get event statistics
   * @returns {Object} Event statistics
   */
  getEventStats() {
    return {
      totalListeners: this.eventListeners.size,
      listenerTypes: Array.from(this.eventListeners.keys()).map(
        (key) => key.split("-")[0],
      ),
      activeListeners: Array.from(this.eventListeners.keys()),
    };
  }

  /**
   * Reset event manager state
   */
  reset() {
    this.cleanup();
    this.eventHandlers.clear();
  }
}

// Export singleton instance
export const eventManager = new EventManager();
